import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import { ArrowRight, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import easyekoLogo from "@/assets/easyeko-logo.svg"
import { login, type LoginCredentials } from "./api"
import { loginSchema, otpStepSchema, type LoginFormValues, type OtpFormValues } from "./schema"
import { useAuthStore } from "./store"

type Step = "credentials" | "otp"

const FONT_FAMILY = "'Manrope', ui-sans-serif, system-ui, sans-serif"
const SERIF_FONT = "'Instrument Serif', ui-serif, Georgia, serif"

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const setUser = useAuthStore((s) => s.setUser)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const [step, setStep] = useState<Step>("credentials")
  const [credentials, setCredentials] = useState<{ username: string; password: string } | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)

  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/"

  const credentialsForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: "", password: "" },
  })

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpStepSchema),
    defaultValues: { otp_code: "" },
  })

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (user) => {
      setUser(user)
      navigate(from, { replace: true })
    },
    onError: (error: unknown, variables: LoginCredentials) => {
      const status = (error as { response?: { status?: number; data?: { error?: string } } })?.response?.status
      const errorCode = (error as { response?: { data?: { error?: string } } })?.response?.data?.error

      if (status === 401 && errorCode === "two_factor_required") {
        setCredentials({ username: variables.username, password: variables.password })
        setStep("otp")
        setServerError(null)
        otpForm.reset({ otp_code: "" })
        return
      }
      if (status === 401) {
        if (step === "otp") {
          setServerError("Nieprawidłowy kod 2FA. Spróbuj ponownie.")
        } else {
          setServerError("Niepoprawne dane logowania")
        }
        return
      }
      if (status === 403) setServerError("Brak dostępu do CRM EasyEko")
      else if (status === 503) setServerError("Serwis chwilowo niedostępny")
      else setServerError("Coś poszło nie tak. Spróbuj ponownie.")
    },
  })

  if (isAuthenticated) return <Navigate to={from} replace />

  const submitCredentials = credentialsForm.handleSubmit((values) => {
    setServerError(null)
    loginMutation.mutate({ username: values.username, password: values.password })
  })

  const submitOtp = otpForm.handleSubmit((values) => {
    if (!credentials) return
    setServerError(null)
    loginMutation.mutate({ ...credentials, otpCode: values.otp_code })
  })

  const cancelOtp = () => {
    setStep("credentials")
    setCredentials(null)
    setServerError(null)
    otpForm.reset({ otp_code: "" })
  }

  return (
    <div
      className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white"
      style={{ fontFamily: FONT_FAMILY }}
    >
      <BrandPanel />

      {/* Form side */}
      <div className="relative flex items-center justify-center px-6 py-10 lg:px-12">
        {/* Mobile-only logo bar */}
        <div className="absolute top-6 left-6 lg:hidden flex items-center gap-3">
          <img src={easyekoLogo} alt="EasyEKO" className="h-8 w-auto" />
          <span className="text-[11px] font-bold tracking-[0.25em] text-emerald-700 border-l border-slate-300 pl-3">
            CRM
          </span>
        </div>

        <div className="w-full max-w-sm animate-in fade-in slide-in-from-bottom-3 duration-500">
          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              {step === "credentials" ? "Logowanie" : "Weryfikacja 2FA"}
            </span>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">
              EasyCRM
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">
              {step === "credentials"
                ? "Zaloguj się, aby kontynuować pracę."
                : "Wpisz 6-cyfrowy kod z aplikacji uwierzytelniającej."}
            </p>
          </div>

          {step === "credentials" ? (
            <form
              onSubmit={submitCredentials}
              action="#"
              method="POST"
              className="space-y-5"
            >
              <div className="space-y-1.5">
                <Label
                  htmlFor="username"
                  className="text-[11px] font-semibold uppercase tracking-wider text-slate-600"
                >
                  Nazwa użytkownika
                </Label>
                <Input
                  id="username"
                  type="text"
                  autoComplete="username"
                  placeholder="np. jan.kowalski"
                  className="h-11 bg-white border-slate-200 shadow-xs focus-visible:border-emerald-500 focus-visible:ring-emerald-500/20 focus-visible:ring-[3px]"
                  disabled={loginMutation.isPending}
                  {...credentialsForm.register("username")}
                />
                {credentialsForm.formState.errors.username && (
                  <p className="text-xs text-rose-600">
                    {credentialsForm.formState.errors.username.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="password"
                  className="text-[11px] font-semibold uppercase tracking-wider text-slate-600"
                >
                  Hasło
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="h-11 bg-white border-slate-200 shadow-xs focus-visible:border-emerald-500 focus-visible:ring-emerald-500/20 focus-visible:ring-[3px]"
                  disabled={loginMutation.isPending}
                  {...credentialsForm.register("password")}
                />
                {credentialsForm.formState.errors.password && (
                  <p className="text-xs text-rose-600">
                    {credentialsForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {serverError && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {serverError}
                </div>
              )}

              <Button
                type="submit"
                className="group w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm shadow-emerald-600/20"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? "Logowanie…" : "Zaloguj się"}
                <ArrowRight className="ml-1.5 size-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </form>
          ) : (
            <form
              onSubmit={submitOtp}
              action="#"
              method="POST"
              className="space-y-5"
            >
              <div className="space-y-1.5">
                <Label
                  htmlFor="otp_code"
                  className="text-[11px] font-semibold uppercase tracking-wider text-slate-600"
                >
                  Kod weryfikacyjny
                </Label>
                <Input
                  id="otp_code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  placeholder="000000"
                  className="h-14 text-center text-3xl tracking-[0.5em] font-mono bg-white border-slate-200 shadow-xs focus-visible:border-emerald-500 focus-visible:ring-emerald-500/20 focus-visible:ring-[3px]"
                  disabled={loginMutation.isPending}
                  autoFocus
                  {...otpForm.register("otp_code")}
                />
                {otpForm.formState.errors.otp_code && (
                  <p className="text-xs text-rose-600">
                    {otpForm.formState.errors.otp_code.message}
                  </p>
                )}
              </div>

              {serverError && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {serverError}
                </div>
              )}

              <div className="space-y-2">
                <Button
                  type="submit"
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm shadow-emerald-600/20"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? "Sprawdzanie…" : "Zweryfikuj"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full h-10 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  onClick={cancelOtp}
                  disabled={loginMutation.isPending}
                >
                  Wróć do logowania
                </Button>
              </div>
            </form>
          )}

          <div className="mt-10 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="size-3.5" />
            Połączenie szyfrowane. Sesja wygasa po 24 h bezczynności.
          </div>
        </div>
      </div>
    </div>
  )
}

function BrandPanel() {
  return (
    <div
      className="relative hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-white overflow-hidden"
    >
      {/* Dot grid - jak ogniwa PV */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />
      {/* Soft glow akcenty */}
      <div
        aria-hidden
        className="absolute -top-40 -left-32 w-[28rem] h-[28rem] bg-emerald-500/15 rounded-full blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-48 -right-24 w-[32rem] h-[32rem] bg-sky-500/10 rounded-full blur-3xl"
      />

      {/* Logo + CRM */}
      <div className="relative flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-700">
        <img
          src={easyekoLogo}
          alt="EasyEKO"
          className="h-10 w-auto brightness-0 invert"
        />
        <span className="text-[11px] font-bold tracking-[0.28em] text-emerald-300/90 border-l border-emerald-300/30 pl-3 py-1">
          CRM
        </span>
      </div>

      {/* Motto */}
      <div className="relative max-w-md animate-in fade-in slide-in-from-bottom-2 duration-700 delay-150">
        <h2
          className="text-[2.75rem] leading-[1.05] tracking-tight"
          style={{ fontFamily: SERIF_FONT, fontWeight: 400 }}
        >
          Umowy, montaże i wpłaty
          <span className="text-emerald-300"> w jednym miejscu.</span>
        </h2>
        <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
          Od leada do podłączenia paneli - codzienna praca handlowca, technika
          i działu finansów bez przeklejania między arkuszami.
        </p>
      </div>

      {/* Stopka */}
      <div className="relative flex items-center justify-between text-[11px] text-slate-500">
        <span className="font-medium tracking-wide">EasyEKO Sp. z o.o.</span>
        <span className="font-mono text-slate-600">© 2026</span>
      </div>
    </div>
  )
}
